import { safeSetInnerHTML } from '@merlin/lib/js/trusted-types.js';
/**
 * Graph Explorer - Cursor Module
 *
 * Manages the cursor toolbar (left side of tab bar):
 *  - Cursor dropdown (select / create named cursors)
 *  - Edit cursor name input
 *  - Save cursor button (POST /cursors/me/save)
 *  - Save-to-pattern button (POST /cursors/{id}/pattern)
 *  - Reset view button
 *
 * Extracted from explorer-tasks.js.  The task-window UI lives in
 * explorer-chat.js (ExplorerChat).
 *
 * Public API: window.ExplorerCursor
 */

(function (global) {
    'use strict';

    // ============================================
    // Module State
    // ============================================
    const state = {
        cursors: [],
        selectedCursorId: 'me',
        selectedCursorName: 'Me',
        isEditingCursorName: false,
    };

    const STORAGE_KEYS = {
        selectedCursor: 'explorer_selected_cursor',
    };

    // ============================================
    // Initialization
    // ============================================
    function init(options = {}) {
        console.log('[ExplorerCursor] Initializing...');
        loadSavedSelections();
        createToolbarElements();
        bindEvents();
        loadCursors();
        console.log('[ExplorerCursor] Initialized');
    }

    // ============================================
    // Persistence
    // ============================================
    function loadSavedSelections() {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEYS.selectedCursor);
            if (raw) {
                const saved = JSON.parse(raw);
                state.selectedCursorId = saved.id || 'me';
                state.selectedCursorName = saved.name || 'Me';
            }
        } catch (e) {
            console.warn('[ExplorerCursor] Failed to load saved cursor:', e);
        }
    }

    function saveSelection(value) {
        try {
            sessionStorage.setItem(STORAGE_KEYS.selectedCursor, JSON.stringify(value));
        } catch (e) {
            console.warn('[ExplorerCursor] Failed to save cursor selection:', e);
        }
    }

    // ============================================
    // Toolbar Elements Creation
    // ============================================
    function createToolbarElements() {
        const graphExplorer = document.querySelector('.graph-explorer');
        if (!graphExplorer) {
            console.warn('[ExplorerCursor] No .graph-explorer container found');
            return;
        }

        // Remove any existing cursor toolbar
        document.getElementById('cursor-toolbar-bar')?.remove();

        const cursorBar = document.createElement('nav');
        cursorBar.id = 'cursor-toolbar-bar';
        cursorBar.className = 'toolbar-cursor';
        cursorBar.setAttribute('role', 'toolbar');
        cursorBar.setAttribute('aria-label', 'Cursor controls');

        safeSetInnerHTML(cursorBar, `
            <!-- Compact trigger button (always visible) -->
            <button class="cursor-trigger" id="cursor-menu-trigger"
                    aria-haspopup="true" aria-expanded="false"
                    data-tooltip="Cursor menu">
                <i data-lucide="mouse-pointer-2"></i>
                <span class="cursor-trigger-name" id="cursors-value">${escapeHtml(state.selectedCursorName)}</span>
                <i data-lucide="chevron-down" class="cursor-trigger-chevron"></i>
            </button>

            <!-- Dropdown panel (hidden by default) -->
            <div class="cursor-menu" id="cursor-menu">
                <!-- Cursor selector -->
                <div class="cursor-menu-header">Select Cursor</div>
                <div class="cursor-menu-search">
                    <input type="text" placeholder="Search cursors..." id="cursors-search">
                </div>
                <div class="cursor-menu-list" id="cursors-list">
                    <div class="cursor-menu-empty">Loading...</div>
                </div>

                <div class="cursor-menu-divider"></div>

                <!-- Inline name edit (toggled by Rename item) -->
                <div class="cursor-menu-edit" id="cursor-edit-row" style="display:none">
                    <input type="text" id="cursor-name-input" placeholder="Cursor name...">
                </div>

                <!-- Action items -->
                <button class="cursor-menu-item" id="edit-cursor-btn">
                    <i data-lucide="pencil"></i>
                    <span>Rename cursor</span>
                </button>
                <button class="cursor-menu-item cursor-menu-save" id="save-cursor-btn">
                    <i data-lucide="save"></i>
                    <span>Save cursor</span>
                </button>
                <button class="cursor-menu-item cursor-menu-pattern" id="save-pattern-btn">
                    <i data-lucide="git-branch"></i>
                    <span>Save to pattern</span>
                </button>

                <div class="cursor-menu-divider"></div>

                <button class="cursor-menu-item cursor-menu-reset" id="reset-cursor-btn">
                    <i data-lucide="rotate-ccw"></i>
                    <span>Reset view</span>
                </button>
            </div>
        `);

        graphExplorer.appendChild(cursorBar);

        if (typeof lucide !== 'undefined') {
            setTimeout(() => lucide.createIcons(), 10);
        }
    }

    // ============================================
    // Event Binding
    // ============================================
    function bindEvents() {
        document.addEventListener('click', handleDocumentClick);

        // Cursor search filter
        document.getElementById('cursors-search')?.addEventListener('input', (e) => {
            filterCursorList(e.target.value);
        });

        // Edit cursor name button (inside dropdown menu)
        document.getElementById('edit-cursor-btn')?.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleCursorNameEdit(!state.isEditingCursorName);
        });

        // Cursor name inline input
        const cursorNameInput = document.getElementById('cursor-name-input');
        if (cursorNameInput) {
            cursorNameInput.addEventListener('keydown', (e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                    saveCursorName(cursorNameInput.value);
                } else if (e.key === 'Escape') {
                    toggleCursorNameEdit(false);
                }
            });
            cursorNameInput.addEventListener('blur', () => {
                if (state.isEditingCursorName) {
                    saveCursorName(cursorNameInput.value);
                }
            });
        }

        // Save cursor button
        document.getElementById('save-cursor-btn')?.addEventListener('click', (e) => {
            e.stopPropagation();
            saveCursor();
        });

        // Save to pattern button
        document.getElementById('save-pattern-btn')?.addEventListener('click', (e) => {
            e.stopPropagation();
            saveToPattern();
        });

        // Reset view button
        document.getElementById('reset-cursor-btn')?.addEventListener('click', (e) => {
            e.stopPropagation();
            resetCursorView();
        });

        // Close menu on Escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                closeCursorMenu();
                if (state.isEditingCursorName) {
                    toggleCursorNameEdit(false);
                }
            }
        });
    }

    function handleDocumentClick(e) {
        // Trigger button toggles the menu
        const trigger = e.target.closest('#cursor-menu-trigger');
        if (trigger) {
            e.preventDefault();
            e.stopPropagation();
            toggleCursorMenu();
            return;
        }

        // Cursor item inside the dropdown list
        const dropdownItem = e.target.closest('#cursors-list .dropdown-item');
        if (dropdownItem) {
            e.preventDefault();
            e.stopPropagation();
            const id = dropdownItem.dataset.id;
            const name = dropdownItem.dataset.name;
            if (id) {
                selectCursor(id, name);
                closeCursorMenu();
            }
            return;
        }

        // Click inside the menu panel — keep open
        const menu = e.target.closest('#cursor-menu');
        if (menu) return;

        // Click outside → close
        closeCursorMenu();
    }

    // ============================================
    // Menu State (replaces old per-dropdown toggle)
    // ============================================
    function toggleCursorMenu() {
        const menu = document.getElementById('cursor-menu');
        const trigger = document.getElementById('cursor-menu-trigger');
        if (!menu) return;

        const isOpen = menu.classList.contains('open');
        if (isOpen) {
            closeCursorMenu();
        } else {
            menu.classList.add('open');
            trigger?.setAttribute('aria-expanded', 'true');
            trigger?.querySelector('.cursor-trigger-chevron')?.classList.add('open');
            setTimeout(() => document.getElementById('cursors-search')?.focus(), 50);
        }
    }

    function closeCursorMenu() {
        const menu = document.getElementById('cursor-menu');
        const trigger = document.getElementById('cursor-menu-trigger');
        menu?.classList.remove('open');
        trigger?.setAttribute('aria-expanded', 'false');
        trigger?.querySelector('.cursor-trigger-chevron')?.classList.remove('open');
    }

    function filterCursorList(query) {
        const items = document.querySelectorAll('#cursors-list .dropdown-item');
        const q = query.toLowerCase();
        items.forEach(item => {
            const name = (item.dataset.name || '').toLowerCase();
            item.style.display = name.includes(q) ? '' : 'none';
        });
    }

    // ============================================
    // Cursor Inline Name Edit
    // ============================================
    function toggleCursorNameEdit(enable) {
        const editRow = document.getElementById('cursor-edit-row');
        const input = document.getElementById('cursor-name-input');
        const editBtn = document.getElementById('edit-cursor-btn');
        if (!editRow || !input) return;

        state.isEditingCursorName = enable;

        if (enable) {
            editRow.style.display = 'block';
            editBtn.style.display = 'none';
            input.value = state.selectedCursorName;
            input.focus();
            input.select();
        } else {
            editRow.style.display = 'none';
            editBtn.style.display = '';
        }
    }

    async function saveCursorName(newName) {
        if (!newName || !newName.trim()) {
            toggleCursorNameEdit(false);
            return;
        }
        newName = newName.trim();
        if (state.selectedCursorId === 'me') {
            await saveCursor(newName);
        } else {
            state.selectedCursorName = newName;
            const valueEl = document.getElementById('cursors-value');
            if (valueEl) valueEl.textContent = newName;
            saveSelection({ id: state.selectedCursorId, name: newName });
        }
        toggleCursorNameEdit(false);
    }

    // ============================================
    // Data: Load Cursors
    // ============================================
    async function loadCursors() {
        const list = document.getElementById('cursors-list');
        if (!list) return;

        if (!isApiConfigured()) {
            safeSetInnerHTML(list, '<div class="dropdown-empty">API not configured</div>');
            return;
        }

        try {
            const config = getApiConfig();
            const url = `${config.apiEndpoint}/workspaces/${config.workspaceId}/cursors/`;
            const response = await apiFetch(url);
            state.cursors = Array.isArray(response)
                ? response
                : (response?.items || response?.cursors || []);
            renderCursorsList();
        } catch (err) {
            console.warn('[ExplorerCursor] Failed to load cursors:', err);
            safeSetInnerHTML(list, '<div class="dropdown-empty">Failed to load</div>');
        }
    }

    // ============================================
    // Cursor Selection
    // ============================================
    function selectCursor(cursorId, cursorName, options = {}) {
        state.selectedCursorId = cursorId;
        state.selectedCursorName = cursorName || (cursorId === 'me' ? 'Me' : cursorId);

        const valueEl = document.getElementById('cursors-value');
        if (valueEl) valueEl.textContent = state.selectedCursorName;

        saveSelection({ id: cursorId, name: state.selectedCursorName });
        renderCursorsList();
        closeCursorMenu();

        document.dispatchEvent(new CustomEvent('explorer:cursorChanged', {
            detail: { id: cursorId, name: state.selectedCursorName },
            bubbles: true
        }));

        console.log('[ExplorerCursor] Selected cursor:', cursorId, state.selectedCursorName);

        if (options.skipReload) {
            return;
        }

        if (typeof ExplorerAPI !== 'undefined') {
            ExplorerAPI.setConfig({ cursorId });
            Promise.resolve()
                .then(() => ExplorerAPI.refreshGraph?.(true))
                .catch((err) => {
                    console.warn('[ExplorerCursor] Failed to refresh graph after cursor switch:', err);
                });
        }
        if (typeof ExplorerToolbar !== 'undefined' && ExplorerToolbar.loadCheckpointsAndBookmarks) {
            ExplorerToolbar.loadCheckpointsAndBookmarks();
        }
        if (typeof ExplorerWindows !== 'undefined' && ExplorerWindows.loadCheckpointsAndBookmarks) {
            ExplorerWindows.loadCheckpointsAndBookmarks();
        }
    }

    function syncCursorSelection(cursorId, cursorName = null) {
        if (!cursorId) return;
        selectCursor(cursorId, cursorName, { skipReload: true });
    }

    // ============================================
    // Rendering
    // ============================================
    function renderCursorsList() {
        const list = document.getElementById('cursors-list');
        if (!list) return;

        const meItem = `
            <div class="dropdown-item ${state.selectedCursorId === 'me' ? 'active' : ''}"
                 data-id="me"
                 data-name="Me"
                 role="option"
                 aria-selected="${state.selectedCursorId === 'me'}">
                <div class="dropdown-item-icon"><i data-lucide="user"></i></div>
                <div class="dropdown-item-content">
                    <span class="dropdown-item-name">Me</span>
                    <span class="dropdown-item-meta">Default cursor (ephemeral)</span>
                </div>
                ${state.selectedCursorId === 'me' ? '<i data-lucide="check" class="dropdown-item-check"></i>' : ''}
            </div>
        `;

        const cursorItems = state.cursors
            .filter(c => c.name !== '_default_')
            .map(cursor => {
                const displayName = cursor.name || cursor.id;
                const isActive = cursor.id === state.selectedCursorId;
                return `
                    <div class="dropdown-item ${isActive ? 'active' : ''}"
                         data-id="${escapeHtml(cursor.id)}"
                         data-name="${escapeHtml(displayName)}"
                         role="option"
                         aria-selected="${isActive}">
                        <div class="dropdown-item-icon"><i data-lucide="mouse-pointer-2"></i></div>
                        <div class="dropdown-item-content">
                            <span class="dropdown-item-name">${escapeHtml(displayName)}</span>
                            ${cursor.created_at ? `<span class="dropdown-item-meta">${formatTimeAgo(cursor.created_at)}</span>` : ''}
                        </div>
                        ${isActive ? '<i data-lucide="check" class="dropdown-item-check"></i>' : ''}
                    </div>
                `;
            }).join('');

        safeSetInnerHTML(list, meItem + cursorItems);

        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }
    }

    // ============================================
    // Save Cursor
    // ============================================
    async function saveCursor(name = null) {
        if (!isApiConfigured()) {
            showNotification('API not configured', 'error');
            return;
        }

        const cursorName = name || state.selectedCursorName;

        if (state.selectedCursorId === 'me') {
            try {
                const config = getApiConfig();
                const saveName = cursorName === 'Me'
                    ? prompt('Enter a name for the new cursor:', 'My Cursor')
                    : cursorName;
                if (!saveName) return;

                const url = `${config.apiEndpoint}/workspaces/${config.workspaceId}/cursors/me/save`;
                const response = await apiFetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: saveName, transfer_tasks: true })
                });

                if (response?.id) {
                    showNotification(`Cursor "${saveName}" saved successfully`, 'success');
                    selectCursor(response.id, saveName);
                    // Route subsequent graph queries to the new cursor
                    if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.setConfig) {
                        ExplorerAPI.setConfig({ cursorId: response.id });
                    }
                    loadCursors();
                }
            } catch (err) {
                console.error('[ExplorerCursor] Failed to save cursor:', err);
                showNotification('Failed to save cursor', 'error');
            }
        } else {
            showNotification(`Cursor "${cursorName}" state saved`, 'success');
        }
    }

    // ============================================
    // Save to Pattern
    // ============================================
    async function saveToPattern() {
        if (!isApiConfigured()) {
            showNotification('API not configured', 'error');
            return;
        }

        const patternName = prompt('Enter a name for the pattern:');
        if (!patternName) return;

        try {
            const config = getApiConfig();
            const apiConfig = (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.getConfig)
                ? ExplorerAPI.getConfig()
                : {};
            const cursorId = apiConfig.cursorId || state.selectedCursorId || 'me';
            const url = `${config.apiEndpoint}/workspaces/${config.workspaceId}/cursors/${cursorId}/pattern`;

            const response = await apiFetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: patternName,
                    description: `Pattern created from cursor ${state.selectedCursorName}`
                })
            });

            if (response) {
                showNotification(`Pattern "${patternName}" created successfully`, 'success');
            }
        } catch (err) {
            console.error('[ExplorerCursor] Failed to save pattern:', err);
            showNotification('Failed to save pattern', 'error');
        }
    }

    // ============================================
    // Reset Cursor View
    // ============================================
    async function resetCursorView() {
        if (!isApiConfigured()) {
            showNotification('API not configured', 'error');
            return;
        }

        try {
            const config = getApiConfig();
            const cursorId = state.selectedCursorId || 'me';

            // Close the cursor menu
            closeCursorMenu();

            // Close the chat panel if open
            if (typeof ExplorerChat !== 'undefined' && ExplorerChat.close) {
                ExplorerChat.close();
            }

            // Switch to Graph tab
            if (typeof ExplorerTabs !== 'undefined' && ExplorerTabs.switchTab) {
                ExplorerTabs.switchTab('graph');
            }

            if (cursorId === 'me' && typeof ExplorerAPI !== 'undefined' && ExplorerAPI.resetMeCursor) {
                console.log('[ExplorerCursor] Resetting cursor using ExplorerAPI...');
                await ExplorerAPI.resetMeCursor();

                // Clear graph
                if (typeof GraphExplorer !== 'undefined' && GraphExplorer.state) {
                    GraphExplorer.state.nodes?.clear();
                    GraphExplorer.state.edges?.clear();
                    GraphExplorer.showEmptyState?.();
                }

                // Clear aliases
                ExplorerAPI.updateAliasesList?.([]);

                // Clear checkpoints / bookmarks
                if (typeof ExplorerToolbar !== 'undefined') {
                    ExplorerToolbar.updateCheckpointsList?.([]);
                    ExplorerToolbar.updateBookmarksList?.([]);
                }

                // Clear windows state
                if (typeof ExplorerWindows !== 'undefined') {
                    ExplorerWindows.clearCheckpointsAndBookmarks?.();
                    ExplorerWindows.loadFqlHistory?.();
                }

                // Reset the chat panel (clear messages + reload tasks/agents)
                if (typeof ExplorerChat !== 'undefined' && ExplorerChat.resetChat) {
                    ExplorerChat.resetChat();
                }

                showNotification('View reset successfully', 'success');
                return;
            }

            // Named cursor reset
            const url = `${config.apiEndpoint}/workspaces/${config.workspaceId}/cursors/${cursorId}/reset`;
            const response = await apiFetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            if (response) {
                // Reset the chat panel (clear messages + reload tasks/agents)
                if (typeof ExplorerChat !== 'undefined' && ExplorerChat.resetChat) {
                    ExplorerChat.resetChat();
                }

                showNotification('View reset successfully', 'success');
                if (typeof ExplorerCore !== 'undefined' && ExplorerCore.refreshGraph) {
                    ExplorerCore.refreshGraph();
                }
            }
        } catch (err) {
            console.error('[ExplorerCursor] Failed to reset cursor:', err);
            showNotification('Failed to reset view', 'error');
        }
    }

    // ============================================
    // API Helpers (self-contained, delegate to ExplorerAPI when available)
    // ============================================
    function isApiConfigured() {
        if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.isConfigured) {
            return ExplorerAPI.isConfigured();
        }
        const config = getApiConfig();
        return config.apiEndpoint !== null && !!config.workspaceId;
    }

    function getApiConfig() {
        if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.getConfig) {
            return ExplorerAPI.getConfig();
        }
        return {
            apiEndpoint: window.explorerConfig?.apiEndpoint || '',
            workspaceId: window.explorerConfig?.workspaceId || null,
            cursorId: window.explorerConfig?.cursorId || 'me',
        };
    }

    function getAuthHeaders() {
        if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.getAuthHeaders) {
            return ExplorerAPI.getAuthHeaders();
        }
        const headers = { 'Content-Type': 'application/json' };
        try {
            const userStr = sessionStorage.getItem('user');
            if (userStr) {
                const user = JSON.parse(userStr);
                if (user?.token) headers['Authorization'] = `Bearer ${user.token}`;
            }
        } catch (e) { /* ignore */ }
        return headers;
    }

    function shouldIncludeCredentials() {
        if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.shouldIncludeCredentials) {
            return ExplorerAPI.shouldIncludeCredentials();
        }
        return true;
    }

    async function apiFetch(url, options = {}) {
        const headers = { ...getAuthHeaders(), ...options.headers };
        if (options.body && !headers['Content-Type']) {
            headers['Content-Type'] = 'application/json';
        }
        const fetchOptions = { ...options, headers };
        if (shouldIncludeCredentials()) fetchOptions.credentials = 'include';

        const response = await fetch(url, fetchOptions);
        if (!response.ok) {
            const error = await response.text();
            throw new Error(error || `HTTP ${response.status}`);
        }
        return response.json();
    }

    // ============================================
    // UI Helpers
    // ============================================
    function showNotification(message, type = 'info') {
        if (typeof ExplorerUtils !== 'undefined' && ExplorerUtils.showNotification) {
            ExplorerUtils.showNotification(message, type);
        } else if (typeof ExplorerModal !== 'undefined' && ExplorerModal.showToast) {
            ExplorerModal.showToast(message, type);
        } else {
            console.log(`[ExplorerCursor] ${type.toUpperCase()}: ${message}`);
        }
    }

    function escapeHtml(text) {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    function formatTimeAgo(timestamp) {
        if (!timestamp) return '';
        try {
            const date = new Date(timestamp);
            const diff = Date.now() - date;
            const minutes = Math.floor(diff / 60000);
            const hours = Math.floor(diff / 3600000);
            const days = Math.floor(diff / 86400000);
            if (minutes < 1) return 'now';
            if (minutes < 60) return `${minutes}m ago`;
            if (hours < 24) return `${hours}h ago`;
            if (days < 7) return `${days}d ago`;
            return date.toLocaleDateString();
        } catch (e) {
            return '';
        }
    }

    // ============================================
    // Public API
    // ============================================
    global.ExplorerCursor = {
        init,
        loadCursors,
        selectCursor,
        saveCursor,
        saveToPattern,
        resetCursorView,
        getSelectedCursor: () => ({ id: state.selectedCursorId, name: state.selectedCursorName }),
        syncCursorSelection,
    };

})(window);
